/**
 * InfrastructureDashboard.jsx
 * Full server inventory management:
 *   - DataTable-style paginated/searchable table (all columns from Server_dashboard_view.php)
 *   - Read-only inventory display with expandable child details
 *   - Expandable row → ServerChildDetails (10 sub-tabs)
 *   - Sync Reports modal tab
 *   - JSON folder scan trigger
 *   - Add / Delete server actions
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiRefreshCw, FiX, FiPlus, FiTrash2, FiSearch,
  FiChevronDown, FiChevronUp, FiAlertTriangle, FiDownload,
  FiChevronLeft, FiChevronRight, FiFileText
} from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchServers, fetchSyncReports, deleteSyncReports, deleteServerRecord, triggerJsonScan, fetchChildDetails } from './serverApi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AddServerModal, ConfirmDialog } from './ServerModals';
import ServerChildDetails from './ServerChildDetails';
import ServerChatbot from './ServerChatbot';

/* ─── Badge helper ─────────────────────────────────────────────────────────── */
function SyncBadge({ status }) {
  const map = {
    Success: 'bg-emerald-100 text-emerald-800',
    Failed: 'bg-red-100 text-red-800',
    Pending: 'bg-amber-100 text-amber-800',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status || 'Offline'}
    </span>
  );
}

/* ─── Read-only data cell ──────────────────────────────────────────────────── */
function DataCell({ value, className = '' }) {
  const display = value == null || value === '' ? '—' : String(value);
  return (
    <span className={`text-gray-700 ${className}`} title={display !== '—' ? display : undefined}>
      {display}
    </span>
  );
}

/** Pill-style rows-per-page control (matches inventory / sync reports design). */
function PageSizeSelect({ value, onChange, options, allValue = 'All' }) {
  const normalized = value === allValue || value === 'All' ? 'All' : value;
  const widthClass = normalized === 'All'
    ? 'min-w-[4.5rem] pr-9'
    : Number(normalized) >= 25
      ? 'min-w-[4rem] pr-9'
      : 'min-w-[3.75rem] pr-9';

  return (
    <div className="relative inline-flex items-center">
      <select
        value={normalized}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next === 'All' ? allValue : Number(next));
        }}
        className={`appearance-none h-9 ${widthClass} pl-4 rounded-full border border-gray-200 bg-gray-100 text-sm font-medium text-gray-700 outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 cursor-pointer shadow-sm`}
      >
        {options.map((opt) => (
          <option key={String(opt)} value={opt === allValue ? 'All' : opt}>
            {opt === allValue || opt === 'All' ? 'All' : opt}
          </option>
        ))}
      </select>
      <FiChevronDown className="pointer-events-none absolute right-3 w-4 h-4 text-gray-500" />
    </div>
  );
}

function resolveSyncServerName(report) {
  const name = report?.server_id || report?.server_name || '';
  if (name) return name;
  const stem = (report?.filename || '').replace(/\.json$/i, '');
  if (stem && !/^api_upload_/i.test(stem)) return stem;
  return '—';
}

function normalizeSyncReport(report) {
  return { ...report, serverName: resolveSyncServerName(report) };
}

/* ─── SyncReports sub-panel ────────────────────────────────────────────────── */
function SyncReportsPanel() {
  const [reports, setReports] = useState({ success: [], failed: [] });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('success');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'parsed_time', direction: 'desc' });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSyncReports();
      const all = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
      const normalized = all.map(normalizeSyncReport);
      setReports({
        success: normalized.filter((r) => r.status === 'Success'),
        failed: normalized.filter((r) => r.status !== 'Success'),
      });
    } catch (e) {
      console.error('Error fetching sync reports:', e);
      toast.error('Failed to load sync reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
    setSelectAllFiltered(false);
  }, [search, activeTab, pageSize]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const allRows = reports[activeTab] || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) =>
      r.parsed_time?.toLowerCase().includes(q)
      || r.filename?.toLowerCase().includes(q)
      || r.serverName?.toLowerCase().includes(q)
      || r.status?.toLowerCase().includes(q)
      || r.error_details?.toLowerCase().includes(q)
    );
  }, [allRows, search]);

  const sorted = useMemo(() => {
    const { key, direction } = sortConfig;
    const list = [...filtered];
    list.sort((a, b) => {
      let aVal = a[key] ?? '';
      let bVal = b[key] ?? '';
      if (key === 'serverName') {
        aVal = a.serverName ?? '';
        bVal = b.serverName ?? '';
      }
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortConfig]);

  const effectivePageSize = pageSize === 'All' ? (sorted.length || 1) : pageSize;
  const totalPages = Math.max(1, Math.ceil(sorted.length / effectivePageSize));
  const rows = sorted.slice(page * effectivePageSize, (page + 1) * effectivePageSize);

  const filteredIds = useMemo(
    () => sorted.map((r) => r.id).filter((id) => id != null),
    [sorted],
  );
  const pageIds = useMemo(
    () => rows.map((r) => r.id).filter((id) => id != null),
    [rows],
  );
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));
  const selectedCount = selectAllFiltered ? filteredIds.length : selectedIds.size;

  const toggleRow = (id) => {
    if (id == null) return;
    setSelectAllFiltered(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePageAll = () => {
    setSelectAllFiltered(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const selectAllMatching = () => {
    setSelectAllFiltered(true);
    setSelectedIds(new Set(filteredIds));
  };

  const clearSelection = () => {
    setSelectAllFiltered(false);
    setSelectedIds(new Set());
  };

  const sortArrow = (key) => (
    sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''
  );

  const handleDownload = () => {
    if (sorted.length === 0) return;
    const headers = ['Time', 'Filename', 'Server Name', 'Status', 'Error Details'];
    const csvContent = `data:text/csv;charset=utf-8,${headers.join(',')}\n${
      sorted.map((r) => `"${r.parsed_time || ''}","${r.filename || ''}","${r.serverName || ''}","${r.status || ''}","${r.error_details || ''}"`).join('\n')
    }`;
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `sync_reports_${activeTab}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkDeleteClick = () => {
    const ids = selectAllFiltered ? filteredIds : [...selectedIds];
    if (ids.length === 0) {
      toast.info('Select at least one sync report to delete');
      return;
    }
    setPendingDeleteIds(ids);
    setShowDeleteConfirm(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (pendingDeleteIds.length === 0) return;
    setDeleting(true);
    try {
      const res = await deleteSyncReports(pendingDeleteIds);
      if (res.data?.status) {
        toast.success(res.data.msg || 'Selected sync reports deleted');
        clearSelection();
        setShowDeleteConfirm(false);
        setPendingDeleteIds([]);
        await load();
      } else {
        toast.error(res.data?.msg || 'Delete failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete sync reports');
    } finally {
      setDeleting(false);
    }
  };

  const colSpan = activeTab === 'failed' ? 8 : 7;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
      <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            {['success', 'failed'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setActiveTab(t)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${activeTab === t
                  ? (t === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white')
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {t === 'success' ? `✓ Recent Syncs (${reports.success.length})` : `✗ Failed (${reports.failed.length})`}
              </button>
            ))}
          </div>
          {sorted.length > 0 && (
            <div className="flex items-center gap-2 border-l border-gray-300 pl-3">
              <PageSizeSelect
                value={pageSize}
                onChange={setPageSize}
                options={[5, 10, 20, 50, 'All']}
              />
              <span className="text-sm text-gray-500">Entries</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search logs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 w-48 transition shadow-sm"
            />
          </div>
          <button
            type="button"
            onClick={handleBulkDeleteClick}
            disabled={deleting || selectedCount === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FiTrash2 className={`w-3.5 h-3.5 ${deleting ? 'animate-pulse' : ''}`} />
            Delete{selectedCount > 0 ? ` (${selectedCount})` : ''}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-700 bg-green-100 hover:bg-green-200 rounded-lg transition"
          >
            <FiDownload className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition"
          >
            <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
          </button>
        </div>
      </div>
      {selectedCount > 0 && (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 flex flex-wrap items-center justify-between gap-2 text-xs text-amber-900">
          <span>
            {selectAllFiltered
              ? `All ${filteredIds.length} matching entries selected.`
              : `${selectedCount} selected.`}
          </span>
          <div className="flex items-center gap-3">
            {!selectAllFiltered && allPageSelected && filteredIds.length > pageIds.length && (
              <button type="button" onClick={selectAllMatching} className="font-semibold text-red-700 hover:underline">
                Select all {filteredIds.length} matching entries
              </button>
            )}
            <button type="button" onClick={clearSelection} className="font-semibold text-gray-600 hover:underline">
              Clear selection
            </button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        <table className="w-full min-w-max text-left border-collapse whitespace-nowrap text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-gray-700 font-semibold uppercase text-xs tracking-wider">
              <th className="px-4 py-3.5 text-center w-10 border-r border-gray-200">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = somePageSelected && !allPageSelected;
                  }}
                  onChange={togglePageAll}
                  disabled={pageIds.length === 0}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                  title="Select all on this page"
                />
              </th>
              <th className="px-4 py-3.5 text-center w-12 border-r border-gray-200">S.No</th>
              <th className="px-4 py-3.5 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('parsed_time')}>
                Time{sortArrow('parsed_time')}
              </th>
              <th className="px-4 py-3.5 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('filename')}>
                Filename{sortArrow('filename')}
              </th>
              <th className="px-4 py-3.5 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('serverName')}>
                Server Name{sortArrow('serverName')}
              </th>
              <th className="px-4 py-3.5 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                Status{sortArrow('status')}
              </th>
              {activeTab === 'failed' && (
                <th className="px-4 py-3.5 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('error_details')}>
                  Error Details{sortArrow('error_details')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="p-16 text-center">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-emerald-500 border-b-2 border-red-500" />
                    <p className="text-xs font-black text-red-600 uppercase tracking-[0.2em] animate-pulse">Loading sync reports…</p>
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-gray-400 text-sm">No records found</td></tr>
            ) : rows.map((r, idx) => {
              const rowSelected = r.id != null && (selectAllFiltered ? filteredIds.includes(r.id) : selectedIds.has(r.id));
              return (
              <tr key={r.id ?? `${r.filename}-${idx}`} className={`group transition duration-150 ${rowSelected ? 'bg-red-50/40' : 'hover:bg-neutral-50/80'}`}>
                <td className="px-4 py-3 text-center border-r border-gray-100">
                  <input
                    type="checkbox"
                    checked={rowSelected}
                    onChange={() => toggleRow(r.id)}
                    disabled={r.id == null}
                    className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer disabled:opacity-40"
                  />
                </td>
                <td className="px-4 py-3 text-center border-r border-gray-100 text-gray-500">
                  {page * effectivePageSize + idx + 1}
                </td>
                <td className="px-4 py-3 font-mono text-gray-700 border-r border-gray-100">{r.parsed_time || '—'}</td>
                <td className="px-4 py-3 font-medium text-gray-900 border-r border-gray-100">{r.filename || '—'}</td>
                <td className="px-4 py-3 font-semibold text-gray-900 border-r border-gray-100">{r.serverName}</td>
                <td className="px-4 py-3 border-r border-gray-100"><SyncBadge status={r.status} /></td>
                {activeTab === 'failed' && (
                  <td className="px-4 py-3 text-red-600 max-w-md truncate border-r border-gray-100" title={r.error_details}>
                    {r.error_details || '—'}
                  </td>
                )}
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {sorted.length > 0 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-sm text-gray-600">
            Showing {pageSize === 'All' ? 1 : page * effectivePageSize + 1}–
            {pageSize === 'All' ? sorted.length : Math.min((page + 1) * effectivePageSize, sorted.length)} of {sorted.length} entries
          </span>
          {pageSize !== 'All' && sorted.length > pageSize && (() => {
            let startPage = Math.max(0, page - 2);
            let endPage = Math.min(totalPages - 1, page + 2);

            if (endPage - startPage < 4) {
              if (startPage === 0) endPage = Math.min(totalPages - 1, 4);
              else if (endPage === totalPages - 1) startPage = Math.max(0, totalPages - 5);
            }

            const pages = [];
            for (let i = startPage; i <= endPage; i++) pages.push(i);

            return (
              <div className="flex gap-1 items-center">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                  className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft className="w-4 h-4" />
                </button>
                {pages.map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i)}
                    className={`min-w-[28px] h-7 px-2 text-xs font-medium rounded-md border transition-colors ${
                      page === i
                        ? 'bg-[#C61717] border-[#C61717] text-white shadow-sm'
                        : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            );
          })()}
        </div>
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => { if (!deleting) { setShowDeleteConfirm(false); setPendingDeleteIds([]); } }}
        onConfirm={handleConfirmBulkDelete}
        title="Delete selected sync reports?"
        text={`${pendingDeleteIds.length} record(s) will be permanently removed. This action cannot be undone.`}
        confirmText="Yes, Delete!"
        confirmColor="bg-red-600 hover:bg-red-700"
        icon={FiAlertTriangle}
        loading={deleting}
      />
    </div>
  );
}

/* ─── Main component ───────────────────────────────────────────────────────── */
export default function InfrastructureDashboard({ onClose }) {
  const [servers, setServers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [view, setView] = useState('table'); // 'table' | 'sync'
  const [showAdd, setShowAdd] = useState(false);
  const [deleteServerId, setDeleteServerId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const loadServers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchServers(search, page, pageSize);
      if (res.data?.data) {
        setServers(res.data.data);
        setTotal(res.data.recordsFiltered ?? res.data.data.length);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, page, pageSize]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedServers = useMemo(() => {
    let sortableItems = [...servers];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [servers, sortConfig]);

  useEffect(() => { loadServers(); }, [loadServers]);

  // Debounce search
  useEffect(() => { setPage(0); }, [search]);

  const handleDeleteClick = (id) => {
    setDeleteServerId(id);
  };

  const handleConfirmDelete = async () => {
    if (!deleteServerId) return;
    try {
      const res = await deleteServerRecord(deleteServerId);
      if (res.data?.status) {
        toast.success('Server record deleted successfully!');
        loadServers();
      } else {
        toast.error('Could not delete record');
      }
    } catch {
      toast.error('Request failed');
    } finally {
      setDeleteServerId(null);
    }
  };

  const handleScan = async () => {
    setLoading(true);
    try {
      const res = await triggerJsonScan();
      if (res.data?.status) {
        toast.success(`Scan Complete: ${res.data.msg}`);
        loadServers();
      } else {
        toast.info(`Notice: ${res.data?.msg || 'Scan completed'}`);
      }
    } catch {
      toast.error('JSON scan failed');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const getHeaderFooterHelper = (doc, title) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const nowStr = `${String(now.getDate()).padStart(2, '0')} ${now.toLocaleString('default', { month: 'short' })} ${now.getFullYear()} ${timeStr}`;
    const drawnPages = new Set();

    return (data) => {
      const currentPage = doc.internal.getNumberOfPages();
      if (!drawnPages.has(currentPage)) {
        // Header
        doc.setFontSize(18);
        doc.setTextColor(180, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text("eNova Software and Hardware Solutions Pvt. Ltd", pageWidth / 2, 35, { align: 'center' });
        doc.setFontSize(13);
        doc.setTextColor(20, 20, 20);
        doc.setFont('helvetica', 'bold');
        doc.text(title, pageWidth / 2, 58, { align: 'center' });
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'normal');
        doc.text(`Report Date: ${dateStr}`, pageWidth / 2, 75, { align: 'center' });
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(30, 88, pageWidth - 30, 88);

        // Footer
        doc.setDrawColor(200, 200, 200);
        doc.line(30, pageHeight - 38, pageWidth - 30, pageHeight - 38);
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(`Report Date: ${nowStr}`, 32, pageHeight - 22);
        doc.text(`Page ${currentPage} of {total_pages_count_string}`, pageWidth - 32, pageHeight - 22, { align: 'right' });

        drawnPages.add(currentPage);
      }
    };
  };

  const handleExportOverallPDF = () => {
    try {
      const doc = new jsPDF('l', 'pt', 'a4');
      const didDrawPage = getHeaderFooterHelper(doc, 'Server Infrastructure Inventory Report');

      const head = [['S.No', 'Server Name', ...PARENT_FIELDS.map(f => f.label), 'Sync Status']];
      const body = sortedServers.map((s, idx) => [
        page * pageSize + idx + 1,
        s.server_name,
        ...PARENT_FIELDS.map(f => s[f.key] || 'N/A'),
        s.sync_status || 'Offline'
      ]);

      autoTable(doc, {
        head,
        body,
        startY: 105,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [51, 51, 51], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { top: 105, bottom: 50, left: 30, right: 30 },
        didDrawPage
      });

      if (typeof doc.putTotalPages === 'function') doc.putTotalPages("{total_pages_count_string}");
      doc.save(`Infrastructure_Report_${new Date().getTime()}.pdf`);
      toast.success('Overall PDF Downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    }
  };

  const handleExportRowPDF = async (srv) => {
    const toastId = toast.loading('Fetching full details and generating PDF...');
    try {
      const res = await fetchChildDetails(srv.id);
      const childData = res.data?.data || res.data || {};

      const doc = new jsPDF('p', 'pt', 'a4');
      const didDrawPage = getHeaderFooterHelper(doc, `Detailed Server Report: ${srv.server_name}`);

      const addKeyValueTable = (title, dataObj, fields) => {
        if (!dataObj || Object.keys(dataObj).length === 0) return;
        const body = fields.map(f => [f.label, dataObj[f.key] || 'N/A']);

        let startY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 25 : 105;
        if (startY > doc.internal.pageSize.getHeight() - 80) {
          doc.addPage();
          startY = 105;
        }

        doc.setFontSize(12);
        doc.setTextColor(40, 40, 40);
        doc.setFont('helvetica', 'bold');
        doc.text(title, 30, startY - 8);

        autoTable(doc, {
          head: [['Field', 'Value']],
          body: body,
          startY: startY,
          styles: { fontSize: 8, cellPadding: 4 },
          headStyles: { fillColor: [80, 80, 80], textColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { top: 105, bottom: 50, left: 30, right: 30 },
          didDrawPage
        });
      };

      const addListTable = (title, list, headers, rowMapper) => {
        if (!list || list.length === 0) return;

        let startY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 25 : 105;
        if (startY > doc.internal.pageSize.getHeight() - 80) {
          doc.addPage();
          startY = 105;
        }

        doc.setFontSize(12);
        doc.setTextColor(40, 40, 40);
        doc.setFont('helvetica', 'bold');
        doc.text(title, 30, startY - 8);

        autoTable(doc, {
          head: [headers],
          body: list.map(rowMapper),
          startY: startY,
          styles: { fontSize: 8, cellPadding: 4 },
          headStyles: { fillColor: [80, 80, 80], textColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { top: 105, bottom: 50, left: 30, right: 30 },
          didDrawPage
        });
      };

      // 1. Parent Summary
      const parentBody = PARENT_FIELDS.map(f => [f.label, srv[f.key] || 'N/A']);
      autoTable(doc, {
        head: [['Field', 'Value']],
        body: parentBody,
        startY: 105,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [80, 80, 80], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { top: 105, bottom: 50, left: 30, right: 30 },
        didDrawPage
      });

      // 2. Asset Info
      const assetFields = [
        { key: 'processor_model', label: 'Processor Model' },
        { key: 'cpu_physical_count', label: 'CPU Physical' },
        { key: 'cpu_core_count', label: 'CPU Cores' },
        { key: 'cpu_thread_count', label: 'CPU Threads' },
        { key: 'kernel_version', label: 'Kernel Version' },
        { key: 'is_virtual', label: 'Virtual/Physical' },
        { key: 'hypervisor_type', label: 'Hypervisor' },
        { key: 'purchase_date', label: 'Purchase Date' },
        { key: 'warranty_expiry', label: 'Warranty Expiry' },
        { key: 'purpose', label: 'Purpose' },
        { key: 'notes', label: 'Notes' }
      ];
      addKeyValueTable('Asset Info', childData.asset || {}, assetFields);

      // 3. Storage & Backup
      addListTable('Storage Mounts', childData.storage,
        ['Mount Point', 'Filesystem', 'Total Size', 'Used', 'Free', 'Usage %'],
        st => [st.mount_point, st.filesystem_type, st.total_size, st.used_size, st.free_size, st.usage_percent + '%']
      );

      const backupFields = [
        { key: 'local_backup_path', label: 'Local Backup Path' },
        { key: 'local_backup_size', label: 'Local Backup Size' },
        { key: 'backup_retention_days', label: 'Retention Days' },
        { key: 'last_backup_date', label: 'Last Backup Date' },
        { key: 'last_backup_status', label: 'Last Backup Status' },
        { key: 'dr_server_name', label: 'DR Server Name' },
        { key: 'dr_server_ip', label: 'DR Server IP' },
        { key: 'dr_folder', label: 'DR Folder' },
        { key: 'dr_last_sync_date', label: 'DR Last Sync' },
        { key: 'dr_last_sync_status', label: 'DR Sync Status' }
      ];
      addKeyValueTable('Backup Configuration', childData.backup || {}, backupFields);

      // 4. Network & IP
      const networkFields = [
        { key: 'internal_ip', label: 'Private IPs' },
        { key: 'external_ip', label: 'Public IPs' },
        { key: 'failover_ip', label: 'Failover IP' },
        { key: 'gateway_ip', label: 'Gateway IP' },
        { key: 'dns', label: 'DNS Servers' },
        { key: 'subnet_mask', label: 'Subnet Mask' },
        { key: 'vpn_access', label: 'VPN Access' },
        { key: 'management_url', label: 'Mgmt URL' },
        { key: 'management_ip', label: 'Mgmt IP' },
        { key: 'ssh_port', label: 'SSH Port' },
        { key: 'remote_access_method', label: 'Remote Access' }
      ];
      addKeyValueTable('Network & IP', childData.network || {}, networkFields);

      // 5. Location Details
      const locationFields = [
        { key: 'location_server', label: 'Location Name' },
        { key: 'latitude', label: 'Latitude' },
        { key: 'longitude', label: 'Longitude' },
        { key: 'location_owner_name', label: 'Owner Name' },
        { key: 'location_owner_contact', label: 'Owner Contact' },
        { key: 'support_contact_name', label: 'Support Contact' },
        { key: 'support_contact_phone', label: 'Support Phone' },
        { key: 'support_supervisor_name', label: 'Supervisor Name' },
        { key: 'support_supervisor_phone', label: 'Supervisor Phone' },
        { key: 'facility_type', label: 'Facility Type' },
        { key: 'internet_provider', label: 'Internet Provider' },
        { key: 'power_source', label: 'Power Source' },
        { key: 'rack_position', label: 'Rack Location' },
        { key: 'notes', label: 'Notes' }
      ];
      addKeyValueTable('Location Details', childData.location || {}, locationFields);

      // 6. Services
      addListTable('Services', childData.services,
        ['Service Name', 'Version', 'Ports'],
        s => [s.service_name, s.service_version, s.ports]
      );

      // 7. Applications
      addListTable('Applications', childData.applications,
        ['Domain / VHost', 'App Name', 'App Path', 'Database Used', 'SSL Expiry'],
        a => [a.domain || '—', a.app_name || 'Website', a.app_path || '—', a.db_used || '—', a.ssl_expiry || '—']
      );

      // 8. Security
      const securityFields = [
        { key: 'ufw_status', label: 'UFW Status' },
        { key: 'fail2ban_status', label: 'Fail2ban' },
        { key: 'crowdsec_status', label: 'Crowdsec' },
        { key: 'ssh_root_login_enabled', label: 'SSH Root Login' },
        { key: 'password_auth_enabled', label: 'Password Auth' },
        { key: 'last_security_patch_date', label: 'Last Security Patch' },
        { key: 'ssl_expiring_30_days', label: 'SSL Certs Expiring (<30d)' }
      ];
      addKeyValueTable('Security', childData.security || {}, securityFields);

      // 9. Operations
      const operationsFields = [
        { key: 'reboot_schedule', label: 'Reboot Schedule' },
        { key: 'hardware_status', label: 'Hardware Status' },
        { key: 'uptime', label: 'Uptime' },
        { key: 'last_reboot', label: 'Last Reboot' },
        { key: 'current_load', label: 'Current Load' },
        { key: 'memory_utilization', label: 'Memory Utilization' },
        { key: 'disk_utilization', label: 'Disk Utilization' },
        { key: 'open_ports', label: 'Open Ports' },
        { key: 'cron_jobs_count', label: 'Cron Jobs' },
        { key: 'running_services_count', label: 'Running Services' },
        { key: 'notes', label: 'Notes' }
      ];
      addKeyValueTable('Operations', childData.operations || {}, operationsFields);

      // 10. Web Server
      const webFields = [
        { key: 'web_server_type', label: 'Web Server Type' },
        { key: 'version', label: 'Version' },
        { key: 'listener_type_1', label: 'Listener 1' },
        { key: 'listener_port_1', label: 'Port 1' },
        { key: 'listener_type_2', label: 'Listener 2' },
        { key: 'listener_port_2', label: 'Port 2' }
      ];
      addKeyValueTable('Web Server', childData.web || {}, webFields);

      // 11. Database
      const dbFields = [
        { key: 'db_1_name', label: 'DB 1 Name' },
        { key: 'db_2_name', label: 'DB 2 Name' },
        { key: 'db_type', label: 'DB Type' },
        { key: 'db_version', label: 'DB Version' },
        { key: 'db_username', label: 'DB User' }
      ];
      addKeyValueTable('Database', childData.database || {}, dbFields);

      if (typeof doc.putTotalPages === 'function') doc.putTotalPages("{total_pages_count_string}");
      doc.save(`Server_${srv.server_name}_Details.pdf`);
      toast.dismiss(toastId);
      toast.success('Detailed PDF Downloaded');
    } catch (err) {
      console.error(err);
      toast.dismiss(toastId);
      toast.error('Failed to generate detailed PDF');
    }
  };

  // All columns from reference Server_dashboard_view.php
  const PARENT_FIELDS = [
    { key: 'private_ip', label: 'Private IP' },
    { key: 'public_ip', label: 'Public IP' },
    { key: 'os_version', label: 'OS Version' },
    // { key: 'current_formula_version',   label: 'Firmware Ver.' },
    // { key: 'asset_tag',                 label: 'Asset Tag' },
    // { key: 'server_brand',              label: 'Brand' },
    // { key: 'server_model',              label: 'Model' },
    // { key: 'serial_number',             label: 'Serial No.' },
    { key: 'ram_installed', label: 'RAM Installed' },
    { key: 'mac_model', label: 'MAC Model' },
    { key: 'total_cpu', label: 'Total CPU' },
    { key: 'total_disk_space', label: 'Total Disk' },
    { key: 'available_disk_space', label: 'Avail. Disk' },
    { key: 'available_memory', label: 'Avail. Memory' },
    { key: 'total_swap_memory', label: 'Swap Memory' },
    { key: 'free_swap_memory', label: 'Free Swap' },
    { key: 'tmp_directory', label: 'Tmp Dir' },
    { key: 'tmp_available_size', label: 'Tmp Avail.' },
  ];



  return (
    <div className="p-4 sm:p-6 font-sans min-h-full flex flex-col bg-[#f8f9fb]">
      <div className="space-y-5 flex-1">
        {/* Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Server Infrastructure Inventory</h1>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">Read-only inventory · 10-tab child configs · JSON auto-import</p>

            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg inline-flex shadow-inner">
              <button onClick={() => setView('table')} className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${view === 'table' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Inventory Table</button>
              <button onClick={() => setView('sync')} className={`px-4 py-1.5 text-sm font-semibold rounded-md transition ${view === 'sync' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Sync Reports</button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {view === 'table' && (
              <>
                <div className="flex items-center gap-2 border-r border-gray-300 pr-3 mr-1">
                  <PageSizeSelect
                    value={pageSize}
                    onChange={(v) => { setPageSize(v); setPage(0); }}
                    options={[5, 10, 20, 50, 100, 1000]}
                    allValue={1000}
                  />
                  <span className="text-sm text-gray-500">Entries</span>
                </div>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text" placeholder="Search servers…" value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-600 w-44 shadow-sm"
                  />
                </div>
              </>
            )}
            {view === 'table' && (
              <button onClick={handleScan} disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> JSON Scan
              </button>
            )}
            {view === 'table' && (
              <button onClick={handleExportOverallPDF}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition shadow-sm">
                <FiFileText className="w-4 h-4" /> Export PDF
              </button>
            )}


          </div>
        </div>

        {/* View: Inventory Table */}
        {view === 'table' && (
          <>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                <table className="w-full min-w-max text-left border-collapse whitespace-nowrap text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-gray-700 font-semibold uppercase text-[10px] tracking-wider">
                      <th className="px-3 py-3 text-center w-10 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('id')}>
                        S.No {sortConfig.key === 'id' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                      </th>
                      <th className="px-3 py-3 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('server_name')}>
                        Server Name {sortConfig.key === 'server_name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                      </th>
                      {PARENT_FIELDS.map((f) => (
                        <th key={f.key} className="px-3 py-3 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort(f.key)}>
                          {f.label} {sortConfig.key === f.key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                        </th>
                      ))}

                      <th className="px-3 py-3 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('sync_status')}>
                        Sync Status {sortConfig.key === 'sync_status' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                      </th>
                      <th className="px-3 py-3 border-r border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('last_sync_time')}>
                        Last Sync {sortConfig.key === 'last_sync_time' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                      </th>
                      <th className="px-3 py-3 text-center sticky right-0 bg-gray-50 border-l border-gray-200 z-20 shadow-[-4px_0_8px_rgba(0,0,0,0.02)]">
                        <div className="flex items-center justify-center gap-1.5">
                          <span className="capitalize text-xs font-bold text-gray-700">Action</span>
                          <button onClick={() => setShowAdd(true)}
                            className="w-5 h-5 flex items-center justify-center rounded bg-[#d32f2f] text-white hover:bg-[#b71c1c] transition focus:outline-none shadow-sm"
                            title="Add Server"
                          >
                            <FiPlus className="w-3.5 h-3.5 font-bold" />
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {loading && (
                      <tr>
                        <td colSpan={30} className="p-16 text-center">
                          <div className="flex flex-col items-center justify-center gap-4">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-emerald-500 border-b-2 border-red-500"></div>
                            <p className="text-xs font-black text-red-600 uppercase tracking-[0.2em] animate-pulse">Syncing Details...</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {!loading && servers.length === 0 && (
                      <tr><td colSpan={30} className="p-8 text-center text-gray-400">No servers found</td></tr>
                    )}
                    {!loading && sortedServers.map((srv, idx) => {
                      const isExpanded = expandedId === srv.id;
                      return (
                        <React.Fragment key={`${srv.id}-${idx}`}>
                          <tr className="group hover:bg-neutral-50/80 transition duration-150">
                            <td className="px-3 py-2 text-center border-r border-gray-100 font-mono">
                              <button
                                onClick={() => setExpandedId(isExpanded ? null : srv.id)}
                                className="inline-flex items-center justify-center gap-1 text-gray-500 hover:text-red-700 transition focus:outline-none w-full"
                                title={isExpanded ? "Collapse Details" : "Expand Details"}
                              >
                                {isExpanded ? <FiChevronUp className="w-3.5 h-3.5 text-red-700" /> : <FiChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                                <span className="text-gray-400 font-normal">{page * pageSize + idx + 1}</span>
                              </button>
                            </td>

                            {/* Server Name (read-only display; name is set on add) */}
                            <td className="px-3 py-2 font-semibold text-gray-800 border-r border-gray-100">
                              {srv.server_name}
                            </td>

                            {PARENT_FIELDS.map((f) => (
                              <td key={f.key} className="px-3 py-2 border-r border-gray-100">
                                <DataCell value={srv[f.key]} />
                              </td>
                            ))}



                            <td className="px-3 py-2 border-r border-gray-100"><SyncBadge status={srv.sync_status} /></td>
                            <td className="px-3 py-2 font-mono text-gray-500 border-r border-gray-100">{srv.last_sync_time || '—'}</td>

                            <td className="px-3 py-2 text-center sticky right-0 border-l border-gray-100 z-10 shadow-[-4px_0_8px_rgba(0,0,0,0.05)] transition duration-150 bg-white group-hover:bg-neutral-50/80">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => handleExportRowPDF(srv)}
                                  title="Export Details PDF"
                                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition"
                                >
                                  <FiFileText className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(srv.id)}
                                  title="Delete server record"
                                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expandable child row */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={30} className="bg-gray-50 border-b border-gray-200 p-4">
                                <ServerChildDetails
                                  serverId={srv.id}
                                  readOnly
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  {total > 0 && (
                    <span>
                      Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total} entries
                    </span>
                  )}
                </div>
                {totalPages > 1 && (() => {
                  let startPage = Math.max(0, page - 2);
                  let endPage = Math.min(totalPages - 1, page + 2);

                  if (endPage - startPage < 4) {
                    if (startPage === 0) endPage = Math.min(totalPages - 1, 4);
                    else if (endPage === totalPages - 1) startPage = Math.max(0, totalPages - 5);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) pages.push(i);

                  return (
                    <div className="flex gap-1 items-center">
                      <button
                        disabled={page === 0}
                        onClick={() => setPage((p) => p - 1)}
                        className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <FiChevronLeft className="w-4 h-4" />
                      </button>
                      {pages.map((i) => (
                        <button
                          key={i}
                          onClick={() => setPage(i)}
                          className={`min-w-[28px] h-7 px-2 text-xs font-medium rounded-md border transition-colors ${
                            page === i
                              ? 'bg-[#C61717] border-[#C61717] text-white shadow-sm'
                              : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        disabled={page + 1 >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                        className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <FiChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Floating chatbot */}
            <ServerChatbot />
          </>
        )}

        {/* View: Sync Reports */}
        {view === 'sync' && <SyncReportsPanel />}

        {/* Add Server Modal */}
        <AddServerModal open={showAdd} onClose={() => setShowAdd(false)} onSuccess={loadServers} />

        <ConfirmDialog
          open={!!deleteServerId}
          onClose={() => setDeleteServerId(null)}
          onConfirm={handleConfirmDelete}
          title="Delete Server Record?"
          text="This removes the server inventory entry and all its child configurations. Additionally, any associated JSON file for this server will also be permanently removed. This action cannot be undone."
          confirmText="Yes, Delete!"
          confirmColor="bg-red-600 hover:bg-red-700"
          icon={FiAlertTriangle}
        />
        <ToastContainer position="top-right" autoClose={3000} style={{ zIndex: 9999999, marginTop: '60px' }} />
      </div>

      {/* Footer */}
      {/* <footer className="mt-8 text-xs sm:text-sm text-gray-500 py-3 border-t border-gray-200 bg-transparent w-full">
        &copy; {new Date().getFullYear()} eNova Software & Hardware Solutions. All rights reserved.
      </footer> */}
    </div>
  );
}
